/**
 * VÉRAULT Portal — Client · Schedule a Call (Wave 26.14)
 *
 * Lets a signed-in client book a 45-minute consultation directly inside
 * the member suite — no jump to the public site. Reuses the same Nylas
 * web-elements scheduler that powers /plan, with after-booking redirect
 * back here so the visitor never leaves the portal.
 */
import React from "react";
import { motion } from "framer-motion";
import { CalendarCheck2, ChevronLeft, Phone } from "lucide-react";
import {
  Surface,
  Heading,
  PageHeader,
  EditorialRule,
  PortalButton,
  api,
  motion as portalMotion,
} from "../../index";
import NylasScheduler from "../../../components/public/NylasScheduler";
import { Link, useSearchParams } from "react-router-dom";

const { fadeInUp, stagger } = portalMotion;

const NYLAS_CONFIGURATION_ID =
  process.env.REACT_APP_NYLAS_CONFIGURATION_ID || "aafea4b5-1887-4198-899f-dce1d9edcffd";
const NYLAS_CLIENT_ID =
  process.env.REACT_APP_NYLAS_CLIENT_ID || "ae93a2f7-6506-480c-916a-bd3b914ff84a";
const NYLAS_SCHEDULER_SLUG =
  process.env.REACT_APP_NYLAS_SCHEDULER_SLUG || "verault-travel-45min";
const NYLAS_FALLBACK_URL = `https://book.nylas.com/us/${NYLAS_CLIENT_ID}/${NYLAS_SCHEDULER_SLUG}`;

export default function ClientSchedulePage() {
  const [params] = useSearchParams();
  const [booked, setBooked] = React.useState(params.get("booked") === "1");
  const [profile, setProfile] = React.useState(null);

  // Pull authoritative name + email from /client-portal/profile so the
  // Nylas scheduler can pre-fill those fields and the client doesn't
  // retype what we already know about them.
  React.useEffect(() => {
    let alive = true;
    api
      .get("/client-portal/profile")
      .then(({ data }) => alive && setProfile(data))
      .catch(() => alive && setProfile({}));
    return () => { alive = false; };
  }, []);

  const bookingConfirmationRedirectUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/portal/client/schedule?booked=1`
      : undefined;

  const handleBooking = React.useCallback(() => {
    setBooked(true);
    // Reset any inline confirmation state from Nylas redirect path
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("booked", "1");
      window.history.replaceState({}, "", url.toString());
    } catch {
      // best-effort
    }
  }, []);

  if (booked) {
    return (
      <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10" data-testid="client-schedule-confirmed">
        <motion.div variants={fadeInUp}>
          <PageHeader
            eyebrow="Confirmed"
            title="Your time is held."
            lede="We've sent a calendar invitation and a quiet confirmation by email."
          />
        </motion.div>
        <motion.div variants={fadeInUp}>
          <Surface variant="default" padded className="!p-8 text-center" data-testid="client-schedule-confirmed-card">
            <div className="inline-flex items-center justify-center size-14 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] mb-5">
              <CalendarCheck2 className="size-7" strokeWidth={1.25} />
            </div>
            <h3 className="font-serif text-[24px] text-[hsl(var(--foreground))]">
              See you soon.
            </h3>
            <p className="mt-2 font-accent italic text-[15px] text-[hsl(var(--foreground-secondary))] max-w-md mx-auto">
              Your advisor has been notified. If anything changes on your end,
              the calendar invitation has reschedule and cancel links.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2">
              <PortalButton
                as={Link}
                to="/portal/client"
                variant="ghost"
                size="sm"
                icon={<ChevronLeft className="size-3.5" strokeWidth={1.5} />}
                data-testid="schedule-back-home"
              >
                Back to your suite
              </PortalButton>
              <PortalButton
                variant="quiet"
                size="sm"
                onClick={() => setBooked(false)}
                data-testid="schedule-book-another"
              >
                Book another time
              </PortalButton>
            </div>
          </Surface>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10" data-testid="client-schedule-page">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Schedule a call"
          title="Find a moment that suits you."
          lede="A quiet 45-minute conversation with your advisor. Reschedule any time — the calendar invitation will have the links."
        />
      </motion.div>

      <motion.div variants={fadeInUp}>
        <EditorialRule label="Choose a time" />
        {/* Wave 30.4.5 — Editorial frame around the Nylas embed.
            Soft ivory bleed (no SaaS card border), thin warm-gold
            divider above and below, Cormorant italic timezone whisper.
            Makes the scheduler feel like a private invitation rather
            than a third-party widget. */}
        <div
          className="mt-6 relative bg-[hsl(var(--background))] dark:bg-[hsl(var(--background-paper))] rounded-md overflow-hidden"
          data-testid="client-nylas-frame"
        >
          {/* Top hairline — warm gold whisper */}
          <div className="h-px bg-gradient-to-r from-transparent via-[hsl(var(--accent-gold))]/35 to-transparent" />

          <div className="px-2 sm:px-4 py-3">
            <p className="text-center font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
              Times are shown in your local timezone — your advisor will adapt to it.
            </p>
          </div>

          <div data-testid="client-nylas-embed">
            <NylasScheduler
              configurationId={NYLAS_CONFIGURATION_ID}
              clientId={NYLAS_CLIENT_ID}
              bookingConfirmationRedirectUrl={bookingConfirmationRedirectUrl}
              fallbackUrl={NYLAS_FALLBACK_URL}
              prefilledName={profile?.full_name || profile?.first_name || ""}
              prefilledEmail={profile?.email || ""}
              testid="client-nylas"
              onBooking={handleBooking}
            />
          </div>

          {/* Bottom hairline — mirrors the top */}
          <div className="h-px bg-gradient-to-r from-transparent via-[hsl(var(--accent-gold))]/35 to-transparent" />
        </div>
        <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
          <Phone className="inline size-3 mr-1 -mt-0.5" strokeWidth={1.5} />
          Prefer email?{" "}
          <a
            href={NYLAS_FALLBACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-4 hover:text-[hsl(var(--foreground))]"
            data-testid="schedule-fallback-link"
          >
            Open in a new tab
          </a>
        </p>
      </motion.div>
    </motion.div>
  );
}
